import { useEffect, useRef, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { FaTimes, FaUpload } from "react-icons/fa";
import { fetchWithAuth } from "../api/fetchWithAuth";

const initialForm = {
  fileName: "",
  title: "",
  description: "",
  category: "",
  referenceType: ""
};

const categoryOptions = ["ERA", "EN&IEC", "ERAv3", "ERAv4", "EU", "Other"];
const referenceTypeOptions = ["mandatory", "informative"];
const maxUploadBytes = 15 * 1024 * 1024;

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.onerror = () => reject(new Error("Could not read the selected file"));
    reader.onabort = () => reject(new Error("File reading was cancelled"));
    reader.readAsDataURL(file);
  });
}

function DocumentMetadataFormModal({ show, onClose, onSaved, document }) {
  const metadata = document?.metadata;
  const isEdit = Boolean(metadata?.id);
  const [form, setForm] = useState(initialForm);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setForm({
      fileName: document?.fileName ?? "",
      title: metadata?.title ?? "",
      description: metadata?.description ?? "",
      category: metadata?.category === "EN" ? "EN&IEC" : metadata?.category ?? "",
      referenceType: metadata?.referenceType ?? ""
    });
    setError("");
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [document?.fileName, metadata, show]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((current) => ({
      ...current,
      [name]: value
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf") || !file.size || file.size > maxUploadBytes) {
      setError("Select a non-empty PDF file up to 15 MB");
      event.target.value = "";
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
    setForm((current) => ({ ...current, fileName: file.name }));
    setError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSaving) return;
    setError("");

    const fileName = form.fileName.trim();
    if (!fileName && !form.title.trim() && !selectedFile) {
      setError("Title is required when no file name is specified");
      return;
    }

    const endpoint = isEdit
      ? `/api/document-metadata/${metadata.id}`
      : "/api/document-metadata";
    const method = isEdit ? "PUT" : "POST";

    const payload = {
      fileName: fileName || null,
      title: form.title.trim(),
      description: form.description.trim(),
      category: form.category.trim(),
      referenceType: form.referenceType
    };

    setIsSaving(true);
    try {
      if (selectedFile) {
        payload.upload = {
          fileName: selectedFile.name,
          contentBase64: await fileToBase64(selectedFile)
        };
      }
      const res = await fetchWithAuth(endpoint, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const responseError = await res.json().catch(() => null);
        throw new Error(responseError?.error || "Save failed");
      }

      await onSaved();
      onClose();
    } catch (err) {
      setError(err.message || "Save failed");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={() => { if (!isSaving) onClose(); }}>
      <Modal.Header closeButton={!isSaving}>
        <Modal.Title>{document ? "Document metadata" : "Add document"}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <div className="alert alert-danger py-2" role="alert">{error}</div>}
        <Form id="document-metadata-form" onSubmit={handleSubmit}>
          <Form.Group className="mb-3" controlId="document-file-name">
            <Form.Label>File name (optional)</Form.Label>
            <Form.Control
              name="fileName"
              value={form.fileName}
              onChange={handleChange}
              readOnly={Boolean(document?.fileUrl)}
              disabled={isSaving}
              maxLength={255}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="document-title">
            <Form.Label>Title</Form.Label>
            <Form.Control
              name="title"
              autoFocus
              maxLength={255}
              disabled={isSaving}
              required={!form.fileName.trim() && !selectedFile}
              value={form.title}
              onChange={handleChange}
            />
          </Form.Group>

          {!document?.fileUrl && (
            <Form.Group className="mb-3">
              <Form.Label>PDF (optional, max. 15 MB)</Form.Label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                className="d-none"
                aria-label="PDF file"
                onChange={handleFileChange}
                disabled={isSaving}
              />
              <div>
                <Button
                  variant="outline-primary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSaving}
                >
                  <FaUpload className="me-2" aria-hidden="true" />
                  Upload PDF
                </Button>
              </div>
              {selectedFile && (
                <div className="d-flex align-items-center gap-2 mt-2">
                  <span className="text-break flex-grow-1">{selectedFile.name}</span>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="flex-shrink-0"
                    title="Remove selected file"
                    aria-label="Remove selected file"
                    disabled={isSaving}
                    onClick={() => {
                      setSelectedFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                  >
                    <FaTimes aria-hidden="true" />
                  </Button>
                </div>
              )}
            </Form.Group>
          )}

          <Form.Group className="mb-3" controlId="document-description">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              name="description"
              disabled={isSaving}
              value={form.description}
              onChange={handleChange}
            />
          </Form.Group>

          <Form.Group controlId="document-category">
            <Form.Label>Category</Form.Label>
            <Form.Select
              name="category"
              disabled={isSaving}
              value={form.category}
              onChange={handleChange}
            >
              <option value="">Select category</option>
              {categoryOptions.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mt-3" controlId="document-reference-type">
            <Form.Label>Reference type</Form.Label>
            <Form.Select
              name="referenceType"
              disabled={isSaving}
              value={form.referenceType}
              onChange={handleChange}
            >
              <option value="">none</option>
              {referenceTypeOptions.map((referenceType) => (
                <option key={referenceType} value={referenceType}>
                  {referenceType}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="success" type="submit" form="document-metadata-form" disabled={isSaving}>
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default DocumentMetadataFormModal;
